<template>
  <div class="page-container">
    <PageHeader title="模板文件管理">
      <template #actions>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="handleAdd">新增模板文件</el-button>
      </template>
    </PageHeader>

    <SearchPanel>
      <el-select v-model="query.rrmfType" clearable placeholder="模板类型" size="small" @change="handleSearch">
        <el-option v-for="item in typeOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
      </el-select>
      <el-input v-model="query.rrmfName" clearable placeholder="模板名称" size="small" style="width: 200px"
        @keyup.enter.native="handleSearch"></el-input>
      <template #actions>
        <el-button size="small" @click="resetQuery">重置</el-button>
        <el-button type="primary" size="small" @click="handleSearch">查询</el-button>
      </template>
    </SearchPanel>

    <TablePanel>
      <aj-table v-loading="loading" :data="list" :columns="columns" :current-page.sync="query.currentPage"
        :page-size.sync="query.pageSize" :page-sizes="[10, 20, 30, 50]" :total="total" row-id-key="rrmfId" stripe
        use-internal-pagination @page-change="handlePageChange" @operate-btn-click="handleOperateBtnClick"></aj-table>
      <EmptyState v-if="!loading && list.length === 0" title="暂无模板文件" icon="el-icon-document"></EmptyState>
    </TablePanel>

    <!-- 新增弹窗 -->
    <aj-form-add :dialog-visible.sync="addDialogVisible" :form-config="addFormConfig" :form-data="addFormData"
      dialog-title="新增模板文件" dialog-width="80%" label-width="130px" :confirm-loading="submitLoading"
      @confirm="handleAddSubmit" />

    <!-- 编辑弹窗 -->
    <aj-form-edit :dialog-visible.sync="editDialogVisible" :form-config="editFormConfig" :form-data="editFormData"
      dialog-title="编辑模板文件" dialog-width="80%" label-width="130px" :confirm-loading="submitLoading"
      @confirm="handleEditSubmit" />

    <!-- 查看详情弹窗 -->
    <el-dialog v-dialog-drag-resize :visible.sync="viewDialogVisible" width="80%" top="7vh"
      custom-class="detail-dialog" :close-on-click-modal="false" :show-close="false">
      <template #title>
        <div class="detail-dialog-header">
          <span class="detail-dialog-title">模板文件详情</span>
          <i class="dialog-close-btn" @click="viewDialogVisible = false" />
        </div>
      </template>
      <div class="detail-content" v-if="viewData">
        <!-- 基本信息 -->
        <div class="detail-section-title">
          <i class="el-icon-document"></i>
          <span>基本信息</span>
        </div>
        <div class="detail-table detail-table--2col">
          <div class="detail-row">
            <div class="detail-cell detail-cell--label">模板类型</div>
            <div class="detail-cell detail-cell--value">{{ typeMap[viewData.rrmfType] || '-' }}</div>
            <div class="detail-cell detail-cell--label">模板名称</div>
            <div class="detail-cell detail-cell--value">{{ viewData.rrmfName || '-' }}</div>
          </div>
        </div>

        <!-- 文件配置 -->
        <div class="detail-section-title">
          <i class="el-icon-folder-opened"></i>
          <span>文件配置</span>
        </div>
        <div class="detail-table">
          <div class="detail-row">
            <div class="detail-cell detail-cell--label" style="width: 160px;">服务器基础地址</div>
            <div class="detail-cell detail-cell--value">{{ viewData.rrmfBasePath || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label" style="width: 160px;">备注</div>
            <div class="detail-cell detail-cell--value">{{ viewData.rrmfDes || '-' }}</div>
          </div>
          <div class="detail-row">
            <div class="detail-cell detail-cell--label" style="width: 160px;">序号</div>
            <div class="detail-cell detail-cell--value">{{ viewData.rrmfIndex != null ? viewData.rrmfIndex : '-' }}</div>
          </div>
        </div>

        <!-- 图片展示 -->
        <template v-if="viewData.rrmfImages">
          <div class="detail-section-title">
            <i class="el-icon-picture-outline"></i>
            <span>图片</span>
          </div>
          <div class="detail-image-list">
            <el-image
              v-for="(url, idx) in viewData.rrmfImages.split(',').filter(Boolean)"
              :key="idx"
              :src="url"
              fit="cover"
              lazy
              :preview-src-list="viewData.rrmfImages.split(',').filter(Boolean)"
              class="detail-image-item"
            />
          </div>
        </template>

        <!-- 关联报告包 -->
        <div class="detail-section-title">
          <i class="el-icon-files"></i>
          <span>关联报告包</span>
          <span class="detail-section-count" v-if="viewRelatedModels.length">({{ viewRelatedModels.length }})</span>
        </div>
        <div v-if="viewRelatedModels.length" class="detail-related-list">
          <div class="detail-related-item" v-for="item in viewRelatedModels" :key="item.rrmId">
            <div class="detail-related-header">
              <span class="detail-related-name">{{ item.rrmModelName || '-' }}</span>
            </div>
            <div class="detail-related-meta">
              <span><i class="el-icon-office-building"></i> {{ [item.rrmCustomer, item.rrmProvince,
              item.rrmCity].filter(Boolean).join(' / ') || '-' }}</span>
              <span><i class="el-icon-notebook-2"></i> {{ item.rrmCourseType || '-' }}</span>
              <span><i class="el-icon-user"></i> {{ item.createusername || '-' }}</span>
              <span><i class="el-icon-time"></i> {{ item.createtime || '-' }}</span>
            </div>
          </div>
        </div>
        <div v-else class="detail-table">
          <div class="detail-row">
            <div class="detail-cell detail-cell--value" style="text-align: center; color: #b0bec5; padding: 16px;">
              暂无关联的报告包
            </div>
          </div>
        </div>

        <!-- 底部元信息 -->
        <div class="detail-meta">
          <span class="detail-meta__item"><i class="el-icon-user"></i> {{ viewData.createusername || '-' }}</span>
          <span class="detail-meta__item"><i class="el-icon-time"></i> {{ viewData.createtime || '-' }}</span>
        </div>
      </div>
      <template #footer>
        <el-button size="small" @click="viewDialogVisible = false">关 闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  getRtReportModelFileList,
  getRtReportModelFileDetails,
  addRtReportModelFile,
  updateRtReportModelFile,
  deleteRtReportModelFile,
  normalizeModelFilePageResponse
} from '@/api/api'
import { uploadToCos } from '@/utils/cosUpload'
import AjFormAdd from '@/components/ajForm/ajFormAdd.vue'
import AjFormEdit from '@/components/ajForm/ajFormEdit.vue'
import AjTable from '@/components/ajTable/ajTable.vue'
import EmptyState from '@/components/EmptyState.vue'
import PageHeader from '@/components/PageHeader.vue'
import SearchPanel from '@/components/SearchPanel.vue'
import TablePanel from '@/components/TablePanel.vue'

// 模板类型映射
const TYPE_MAP = { 1: '考勤成绩', 2: '评估分数', 3: '总结报告' }

function createEmptyFormData() {
  return {
    rrmfId: null,
    rrmfType: '',
    rrmfName: '',
    rrmfBasePath: '',
    rrmfDes: '',
    rrmfImages: [],
    rrmfIndex: null
  }
}

export default {
  name: 'ReportTemplatePage',
  components: { AjFormAdd, AjFormEdit, AjTable, EmptyState, PageHeader, SearchPanel, TablePanel },
  data() {
    return {
      loading: false,
      submitLoading: false,
      list: [],
      total: 0,
      uploadToCos,

      // 搜索参数
      query: {
        rrmfType: '',
        rrmfName: '',
        currentPage: 1,
        pageSize: 10
      },

      // 模板类型选项（静态枚举）
      typeOptions: [
        { label: '考勤成绩', value: 1 },
        { label: '评估分数', value: 2 },
        { label: '总结报告', value: 3 }
      ],
      typeMap: TYPE_MAP,

      // 新增弹窗
      addDialogVisible: false,
      addFormData: createEmptyFormData(),

      // 编辑弹窗
      editDialogVisible: false,
      editFormData: createEmptyFormData(),

      // 查看弹窗
      viewDialogVisible: false,
      viewData: null,
      viewRelatedModels: []
    }
  },
  computed: {
    columns() {
      return [
        { type: 'index', label: '序号', width: 60 },
        {
          prop: 'rrmfType',
          label: '模板类型',
          width: 120,
          render: (h, { row }) => h('span', TYPE_MAP[row.rrmfType] || '-')
        },
        { prop: 'rrmfName', label: '模板名称', minWidth: 180 },
        {
          prop: 'rrmfBasePath',
          label: '服务器基础地址',
          minWidth: 200,
          showOverflowTooltip: true
        },
        {
          prop: 'rrmfDes',
          label: '备注',
          minWidth: 150,
          showOverflowTooltip: true
        },
        {
          prop: 'rrmfImages',
          label: '图片',
          width: 100,
          render: (h, { row }) => {
            if (!row.rrmfImages) return h('span', { style: 'color: #b0bec5' }, '-')
            // 多张图片取第一张做缩略图预览
            const urls = String(row.rrmfImages).split(',').filter(Boolean)
            if (!urls.length) return h('span', { style: 'color: #b0bec5' }, '-')
            return h('el-image', {
              props: { src: urls[0], fit: 'cover', lazy: true, previewSrcList: urls },
              style: { width: '40px', height: '40px', borderRadius: '4px', display: 'inline-block' }
            })
          }
        },
        {
          prop: 'rrmfIndex',
          label: '序号',
          width: 80,
          render: (h, { row }) => h('span', row.rrmfIndex != null ? row.rrmfIndex : '-')
        },
        { prop: 'createusername', label: '创建人', width: 100 },
        { prop: 'createtime', label: '创建时间', width: 160 },
        {
          type: 'operation',
          prop: 'operation',
          label: '操作',
          width: 200,
          fixed: 'right',
          buttons: [
            { key: 'view', text: '查看', type: 'text' },
            { key: 'edit', text: '编辑', type: 'text' },
            { key: 'delete', text: '删除', type: 'text', class: 'text-danger' }
          ]
        }
      ]
    },
    // 新增表单配置
    addFormConfig() {
      return [
        [
          {
            label: '模板类型',
            prop: 'rrmfType',
            type: 'select',
            span: 12,
            required: true,
            options: this.typeOptions
          },
          {
            label: '模板名称',
            prop: 'rrmfName',
            type: 'input',
            span: 12,
            required: true
          }
        ],
        [
          {
            label: '服务器基础地址',
            prop: 'rrmfBasePath',
            type: 'input',
            span: 24,
            required: true
          }
        ],
        [
          {
            label: '备注',
            prop: 'rrmfDes',
            type: 'textarea',
            span: 24,
            rows: 3
          }
        ],
        // 图片上传到腾讯云 COS
        [
          {
            label: '图片',
            prop: 'rrmfImages',
            type: 'upload',
            span: 24,
            acceptType: 'image/*',
            limit: 5,
            multiple: true,
            uploadRequest: this.uploadToCos,
            uploadBtnText: '选择图片'
          }
        ],
        [
          {
            label: '序号',
            prop: 'rrmfIndex',
            type: 'inputNumber',
            span: 12,
            min: 0,
            placeholder: '请输入序号'
          }
        ]
      ]
    },
    // 编辑表单配置
    editFormConfig() {
      return this.addFormConfig
    }
  },
  created() {
    this.fetchList()
  },
  methods: {
    // ============ 列表 ============
    async fetchList() {
      this.loading = true
      try {
        const params = { currentPage: this.query.currentPage, pageSize: this.query.pageSize }
        if (this.query.rrmfType !== '' && this.query.rrmfType !== null) {
          params.rrmfType = this.query.rrmfType
        }
        if (this.query.rrmfName) {
          params.rrmfName = this.query.rrmfName
        }
        const res = await getRtReportModelFileList(params)
        const pageData = normalizeModelFilePageResponse(res)
        this.list = pageData.list
        this.total = pageData.total
      } catch (error) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.query.currentPage = 1
      this.fetchList()
    },
    resetQuery() {
      this.query = { rrmfType: '', rrmfName: '', currentPage: 1, pageSize: 10 }
      this.fetchList()
    },
    handlePageChange({ currentPage, pageSize }) {
      this.query.currentPage = currentPage
      this.query.pageSize = pageSize
      this.fetchList()
    },

    // ============ 新增 ============
    handleAdd() {
      this.addFormData = createEmptyFormData()
      this.addDialogVisible = true
    },
    async handleAddSubmit({ data }) {
      this.submitLoading = true
      try {
        const params = {
          rrmfType: data.rrmfType,
          rrmfName: data.rrmfName,
          rrmfBasePath: data.rrmfBasePath,
          rrmfDes: data.rrmfDes || '',
          // 图片文件数组转为逗号分隔 URL 字符串
          rrmfImages: this.extractImageUrls(data.rrmfImages),
          rrmfIndex: data.rrmfIndex != null ? data.rrmfIndex : 0
        }
        await addRtReportModelFile(params)
        this.$message.success('模板文件创建成功')
        this.addDialogVisible = false
        this.fetchList()
      } finally {
        this.submitLoading = false
      }
    },

    // ============ 编辑 ============
    async handleEdit(row) {
      try {
        const res = await getRtReportModelFileDetails({ rrmfId: row.rrmfId })
        const detail = res.data || row
        this.editFormData = {
          rrmfId: detail.rrmfId,
          rrmfType: detail.rrmfType || '',
          rrmfName: detail.rrmfName || '',
          rrmfBasePath: detail.rrmfBasePath || '',
          rrmfDes: detail.rrmfDes || '',
          // 逗号分隔的图片 URL 字符串转为文件对象数组供上传组件回显
          rrmfImages: this.parseImageUrls(detail.rrmfImages),
          rrmfIndex: detail.rrmfIndex != null ? detail.rrmfIndex : null
        }
        this.editDialogVisible = true
      } catch (error) {
        this.$message.error('获取模板文件详情失败')
      }
    },
    async handleEditSubmit({ data }) {
      this.submitLoading = true
      try {
        const params = {
          rrmfId: data.rrmfId,
          rrmfType: data.rrmfType,
          rrmfName: data.rrmfName,
          rrmfBasePath: data.rrmfBasePath,
          rrmfDes: data.rrmfDes || '',
          rrmfImages: this.extractImageUrls(data.rrmfImages),
          rrmfIndex: data.rrmfIndex != null ? data.rrmfIndex : 0
        }
        await updateRtReportModelFile(params)
        this.$message.success('模板文件更新成功')
        this.editDialogVisible = false
        this.fetchList()
      } finally {
        this.submitLoading = false
      }
    },

    // ============ 查看 ============
    async handleView(row) {
      try {
        const res = await getRtReportModelFileDetails({ rrmfId: row.rrmfId })
        this.viewData = res.data || row
        // item 为关联的报告包列表
        this.viewRelatedModels = Array.isArray(res.item) ? res.item : []
        this.viewDialogVisible = true
      } catch (error) {
        this.$message.error('获取模板文件详情失败')
      }
    },

    // ============ 删除 ============
    async handleDelete(row) {
      try {
        await this.$confirm('确认删除该模板文件吗？', '提示', { type: 'warning' })
        await deleteRtReportModelFile({ rrmfId: row.rrmfId })
        this.$message.success('模板文件已删除')
        this.fetchList()
      } catch (error) { }
    },

    // ============ 操作按钮分发 ============
    handleOperateBtnClick({ btn, row }) {
      switch (btn.key) {
        case 'view': this.handleView(row); break
        case 'edit': this.handleEdit(row); break
        case 'delete': this.handleDelete(row); break
      }
    },

    // ============ 图片字段辅助方法 ============
    /** 将上传组件返回的文件数组转为逗号分隔的 URL 字符串 */
    extractImageUrls(files) {
      if (!files || !Array.isArray(files)) return ''
      return files.map(f => f.url || '').filter(Boolean).join(',')
    },
    /** 将逗号分隔的图片 URL 字符串转为文件对象数组，供上传组件回显 */
    parseImageUrls(str) {
      if (!str) return []
      return String(str).split(',').filter(Boolean).map(url => ({
        name: url.split('/').pop() || '图片',
        url,
        extension: url.split('.').pop() || '',
        type: 'image'
      }))
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  border: 1px solid #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.text-danger {
  color: #ef4444 !important;
}

/* ========== 详情弹框 ========== */
::v-deep .detail-dialog {
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(15, 23, 42, 0.12);

  .el-dialog__header {
    padding: 0;
    border-bottom: none;
  }

  .el-dialog__body {
    padding: 0;
    max-height: 70vh;
    overflow-x: hidden;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 5px;
    }

    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 5px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }
  }

  .el-dialog__footer {
    padding: 14px 28px;
    border-top: 1px solid #eef1f6;
  }
}

.detail-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px 28px 18px;
  border-bottom: 1px solid #eef1f6;
}

.detail-dialog-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}


.detail-content {
  padding: 20px 28px 24px;
}

.detail-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
  padding-left: 10px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 16px;
    background: linear-gradient(180deg, #4f7cff, #6366f1);
    border-radius: 2px;
  }

  i {
    font-size: 16px;
    color: #4f7cff;
    margin-left: 4px;
  }

  &+.detail-table {
    margin-bottom: 24px;
  }
}

.detail-table {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 24px;

  &--2col {
    .detail-row {
      display: grid;
      grid-template-columns: repeat(2, auto 1fr);
    }
  }
}

.detail-row {
  display: flex;
  border-bottom: 1px solid #e2e8f0;

  &:last-child {
    border-bottom: none;
  }
}

.detail-cell {
  padding: 12px 16px;
  font-size: 13px;
  line-height: 1.6;

  &--label {
    background: #f5f7fa;
    color: #606266;
    font-weight: 500;
    white-space: nowrap;
    min-width: 80px;
    border-right: 1px solid #e2e8f0;
    display: flex;
    align-items: center;
  }

  &--value {
    background: #fff;
    color: #1e293b;
    flex: 1;
    min-width: 0;
    overflow: hidden;
    word-break: break-all;
  }
}

.detail-meta {
  display: flex;
  gap: 28px;
  padding: 14px 0 0;
  border-top: 1px solid #eef1f6;
}

.detail-meta__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #a0aec0;

  i {
    font-size: 13px;
  }
}

// 关联报告包数量标记
.detail-section-count {
  font-size: 12px;
  font-weight: 400;
  color: #94a3b8;
  margin-left: 4px;
}

// 关联报告包列表
.detail-related-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
}

// 详情图片列表
.detail-image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
}

.detail-image-item {
  width: 120px;
  height: 120px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
}

.detail-related-item {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 16px;
  background: #fafbfc;
  transition: border-color 0.15s;

  &:hover {
    border-color: #c7d2e0;
  }
}

.detail-related-header {
  margin-bottom: 8px;
}

.detail-related-name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.detail-related-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    color: #64748b;

    i {
      font-size: 13px;
      color: #94a3b8;
    }
  }
}

::v-deep .aj-table-operation-link {
  margin: 0 4px;
  font-size: 12px;
  font-weight: 500;

  &.text-danger {
    color: #ef4444 !important;

    &:hover {
      color: #dc2626 !important;
    }
  }

  &:not(.text-danger):hover {
    color: #4f7cff !important;
  }
}
</style>
